import { apiClient } from '../api/client';
import type { SelectId } from '../components/MultiSelect';

interface GroupMembersResponse {
  groupId: number;
  kind: 'plain' | 'course' | 'activity';
  members: Array<{ _id: number; name: string }>;
}

/** Current member ids of a group (managed groups resolve virtually server-side). */
export async function fetchGroupMemberIds(groupId: SelectId): Promise<SelectId[]> {
  const { data } = await apiClient.get<GroupMembersResponse>(`/api/groups/${groupId}/members`);
  return (data?.members ?? []).map((m) => m._id);
}

/**
 * Of `memberIds`, those that came from a removed group label and belong to none
 * of the still-selected groups (candidates to drop with the label).
 */
export async function membersOnlyFromRemovedGroups(
  removedGroupIds: SelectId[],
  remainingGroupIds: SelectId[],
  memberIds: SelectId[],
): Promise<number[]> {
  if (removedGroupIds.length === 0 || memberIds.length === 0) return [];
  const [removed, remaining] = await Promise.all([
    Promise.all(removedGroupIds.map(fetchGroupMemberIds)),
    Promise.all(remainingGroupIds.map(fetchGroupMemberIds)),
  ]);
  const fromRemoved = new Set(removed.flat().map(Number));
  const kept = new Set(remaining.flat().map(Number));
  return memberIds.map(Number).filter((id) => fromRemoved.has(id) && !kept.has(id));
}
